import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import { Button, Collapse, Modal, Select, Space, Typography, message } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { CnDatahubPrivateCategory } from './CnDatahubPrivateCategory'
import { CnDatahubSharedCategory } from './CnDatahubSharedCategory'
import { DatahubSaveData, PrivateCategoryConfig, PrivateCategorySource, SharedCategoryConfig } from './datahub-interfaces'
import { DATAHUB_PRIVATE_TITLE_KEY, ensureSharedCategoryFilter, isPrivateTitleColumn } from './datahubDefaults'
import { isFilterJsonValid, parseSelectOptions } from './datahubFilter'
import { clonePrivateCategoryConfig, createEmptyPrivateCategory, fetchPrivateCategorySources } from './datahubCopy'

interface CnDatahubProps {
  orgaId: string
  initialData?: DatahubSaveData | null
}

export interface CnDatahubHandle {
  getSaveData: () => DatahubSaveData
  getValidationErrors: () => string[]
}

function validatePrivateCategories(orgaId: string, categories: PrivateCategoryConfig[]): string[] {
  const errors: string[] = []
  const categoryNames = new Set<string>()
  for (const { category, access, columns } of categories) {
    const name = category.name.trim()
    if (!name) {
      errors.push('Une catégorie privée a un nom vide')
    } else if (categoryNames.has(name)) {
      errors.push(`Nom de catégorie privée dupliqué : "${name}"`)
    }
    categoryNames.add(name)

    if (access.orgaId !== orgaId) {
      errors.push(`L'accès de la catégorie privée "${name || '?'}" n'appartient pas à l'organisation`)
    }
    if (access.categoryId !== category.id) {
      errors.push(`L'accès de la catégorie privée "${name || '?'}" ne référence pas la bonne catégorie`)
    }
    if (columns.length === 0) {
      errors.push(`La catégorie "${name || '?'}" n'a aucune colonne`)
    }

    const titleColumns = columns.filter(isPrivateTitleColumn)
    if (titleColumns.length === 0) {
      errors.push(
        `La catégorie "${name || '?'}" doit contenir une colonne identifiant avec la clé "${DATAHUB_PRIVATE_TITLE_KEY}"`,
      )
    } else if (titleColumns.length > 1) {
      errors.push(
        `La catégorie "${name || '?'}" ne doit contenir qu'une seule colonne "${DATAHUB_PRIVATE_TITLE_KEY}"`,
      )
    } else {
      const titleCol = titleColumns[0]
      if (titleCol.datakey !== DATAHUB_PRIVATE_TITLE_KEY) {
        errors.push(
          `La colonne "${DATAHUB_PRIVATE_TITLE_KEY}" de la catégorie "${name || '?'}" doit avoir le label "${DATAHUB_PRIVATE_TITLE_KEY}"`,
        )
      }
      if (titleCol.type !== 'text') {
        errors.push(
          `La colonne "${DATAHUB_PRIVATE_TITLE_KEY}" de la catégorie "${name || '?'}" doit être de type texte`,
        )
      }
      if (!titleCol.required) {
        errors.push(
          `La colonne "${DATAHUB_PRIVATE_TITLE_KEY}" de la catégorie "${name || '?'}" doit être obligatoire`,
        )
      }
    }

    const colNames = new Set<string>()
    for (const col of columns) {
      const colName = col.name.trim()
      if (!colName) {
        errors.push(`Une colonne de la catégorie "${name || '?'}" a un nom vide`)
      } else if (colNames.has(colName)) {
        errors.push(`Colonne dupliquée "${colName}" dans la catégorie "${name || '?'}"`)
      }
      colNames.add(colName)

      if (col.categoryAccessId != null || col.categoryColumnId != null) {
        errors.push(
          `La colonne "${colName || '?'}" de la catégorie "${name || '?'}" ne doit pas avoir de categoryAccessId / categoryColumnId`,
        )
      }
      if (col.categoryId !== category.id) {
        errors.push(
          `La colonne "${colName || '?'}" de la catégorie "${name || '?'}" référence une autre catégorie`,
        )
      }
      if ((col.type === 'select' || col.type === 'multiple-labels') && (!col.options || parseSelectOptions(col.options).length === 0)) {
        errors.push(
          `La colonne "${colName || '?'}" de la catégorie "${name || '?'}" est de type "liste déroulante" sans options`,
        )
      }
    }
  }
  return errors
}

const CnDatahub = forwardRef<CnDatahubHandle, CnDatahubProps>(function CnDatahub(
  { orgaId, initialData },
  ref,
) {
  const [sharedCategories, setSharedCategories] = useState<SharedCategoryConfig[]>([])
  const [privateCategories, setPrivateCategories] = useState<PrivateCategoryConfig[]>([])
  const [sharedActiveKeys, setSharedActiveKeys] = useState<string[]>([])
  const [privateActiveKeys, setPrivateActiveKeys] = useState<string[]>([])
  const [copyModalOpen, setCopyModalOpen] = useState(false)
  const [copySources, setCopySources] = useState<PrivateCategorySource[]>([])
  const [copySourcesLoading, setCopySourcesLoading] = useState(false)
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)

  useEffect(() => {
    const shared = (initialData?.sharedCategories ?? []).map((cfg) => ({
      ...cfg,
      access: cfg.access ?? null,
      filter: ensureSharedCategoryFilter(cfg),
    }))
    const privates = initialData?.privateCategories ?? []
    setSharedCategories(shared)
    setPrivateCategories(privates)
    setSharedActiveKeys(shared.some((c) => c.access !== null) ? ['shared-categories'] : [])
    setPrivateActiveKeys(privates.length > 0 ? ['private-categories'] : [])
  }, [initialData])

  const getValidationErrors = useCallback((): string[] => {
    const errors: string[] = []
    for (const config of sharedCategories) {
      if (config.access && !isFilterJsonValid(config.filter?.jsonFilter, config.defaultColumns)) {
        errors.push(`Filtre JSON invalide pour la catégorie partagée "${config.category.name}"`)
      }
    }
    errors.push(...validatePrivateCategories(orgaId, privateCategories))
    return errors
  }, [sharedCategories, privateCategories, orgaId])

  useImperativeHandle(
    ref,
    () => ({
      getSaveData: () => ({ sharedCategories, privateCategories }),
      getValidationErrors,
    }),
    [getValidationErrors, privateCategories, sharedCategories],
  )

  const updateSharedCategory = useCallback((index: number, config: SharedCategoryConfig) => {
    setSharedCategories((current) => current.map((item, i) => (i === index ? config : item)))
  }, [])

  const updatePrivateCategory = useCallback((index: number, config: PrivateCategoryConfig) => {
    setPrivateCategories((current) => current.map((item, i) => (i === index ? config : item)))
  }, [])

  const deletePrivateCategory = useCallback((index: number) => {
    setPrivateCategories((current) => current.filter((_, i) => i !== index))
  }, [])

  const addEmptyPrivateCategory = useCallback(() => {
    setPrivateCategories((current) => [...current, createEmptyPrivateCategory(orgaId)])
  }, [orgaId])

  const openCopyModal = useCallback(async () => {
    setCopyModalOpen(true)
    setSelectedSourceId(null)
    setCopySourcesLoading(true)
    try {
      const sources = await fetchPrivateCategorySources(orgaId)
      setCopySources(sources)
    } catch (error) {
      console.error(error)
      message.error('Impossible de charger les catégories privées à copier')
      setCopySources([])
    } finally {
      setCopySourcesLoading(false)
    }
  }, [orgaId])

  const confirmCopyFromSource = useCallback(() => {
    const source =
      copySources.find((cfg) => cfg.category.id === selectedSourceId) ??
      privateCategories.find((cfg) => cfg.category.id === selectedSourceId)
    if (!source) {
      message.warning('Sélectionnez une catégorie à copier')
      return
    }
    setPrivateCategories((current) => [...current, clonePrivateCategoryConfig(source, orgaId)])
    setCopyModalOpen(false)
    setSelectedSourceId(null)
  }, [copySources, privateCategories, orgaId, selectedSourceId])

  const enabledSharedCount = sharedCategories.filter((c) => c.access !== null).length

  const remoteCopyOptions = copySources.map((cfg) => ({
    value: cfg.category.id,
    label: `${cfg.category.name} (${cfg.orgaName})`,
  }))

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Collapse
        size="small"
        activeKey={sharedActiveKeys}
        onChange={(keys) => setSharedActiveKeys(Array.isArray(keys) ? keys : [keys])}
        items={[
          {
            key: 'shared-categories',
            label: (
              <Space size="small">
                <Typography.Text strong>Catégories partagées</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {sharedCategories.length} disponible{sharedCategories.length > 1 ? 's' : ''}
                  {enabledSharedCount > 0 && ` · ${enabledSharedCount} activée${enabledSharedCount > 1 ? 's' : ''}`}
                </Typography.Text>
              </Space>
            ),
            children:
              sharedCategories.length === 0 ? (
                <Typography.Text type="secondary">Aucune catégorie partagée disponible.</Typography.Text>
              ) : (
                sharedCategories.map((config, index) => (
                  <CnDatahubSharedCategory
                    key={config.category.id}
                    config={config}
                    orgaId={orgaId}
                    onChange={(updated) => updateSharedCategory(index, updated)}
                  />
                ))
              ),
          },
        ]}
      />

      <Collapse
        size="small"
        activeKey={privateActiveKeys}
        onChange={(keys) => setPrivateActiveKeys(Array.isArray(keys) ? keys : [keys])}
        items={[
          {
            key: 'private-categories',
            label: (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Space size="small">
                  <Typography.Text strong>Catégories privées</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {privateCategories.length} configurée{privateCategories.length > 1 ? 's' : ''}
                  </Typography.Text>
                </Space>
                <Space size="small" onClick={(e) => e.stopPropagation()}>
                  <Button size="small" onClick={openCopyModal}>
                    Copier depuis…
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={addEmptyPrivateCategory}
                  >
                    Nouvelle catégorie
                  </Button>
                </Space>
              </div>
            ),
            children:
              privateCategories.length === 0 ? (
                <Typography.Text type="secondary">Aucune catégorie privée configurée.</Typography.Text>
              ) : (
                privateCategories.map((config, index) => (
                  <CnDatahubPrivateCategory
                    key={config.category.id}
                    config={config}
                    onChange={(updated) => updatePrivateCategory(index, updated)}
                    onDelete={() => deletePrivateCategory(index)}
                  />
                ))
              ),
          },
        ]}
      />

      <Modal
        title="Copier une catégorie privée"
        open={copyModalOpen}
        onCancel={() => setCopyModalOpen(false)}
        onOk={confirmCopyFromSource}
        okText="Copier"
        cancelText="Annuler"
        okButtonProps={{ disabled: !selectedSourceId }}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Crée une nouvelle catégorie privée pour cette organisation à partir d&apos;une configuration existante.
        </Typography.Paragraph>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Choisir une catégorie source"
          style={{ width: '100%' }}
          loading={copySourcesLoading}
          value={selectedSourceId}
          onChange={setSelectedSourceId}
          options={remoteCopyOptions}
          notFoundContent={
            copySourcesLoading
              ? 'Chargement…'
              : 'Aucune catégorie privée disponible à copier'
          }
        />
      </Modal>
    </Space>
  )
})

export default CnDatahub
